import { Loader2 } from "lucide-react";

export const Panel = ({ title, right, children, className = "", testid }) => (
  <section className={`panel min-w-0 overflow-x-auto p-3 fade-up ${className}`} data-testid={testid}>
    {(title || right) && (
      <div className="mb-3 flex items-center justify-between gap-2">
        {title && <h3 className="font-display text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">{title}</h3>}
        {right}
      </div>
    )}
    {children}
  </section>
);

export const Stat = ({ label, value, sub, tone = "", testid }) => (
  <div className="panel p-3 fade-up" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
    <div className={`mono mt-1 text-xl font-semibold ${tone}`}>{value}</div>
    {sub && <div className="mono mt-0.5 text-[11px] text-slate-500">{sub}</div>}
  </div>
);

const TONES = {
  BUY: "bg-emerald-600 text-white border-emerald-600",
  SELL: "bg-red-600 text-white border-red-600",
  HOLD: "bg-slate-100 text-slate-600 border-slate-300",
  QUALIFIED: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  CONFIRMED: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  FILLED: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  OPEN: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  WORKING: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  ACTIVE: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  DETECTED: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  WATCH: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  FORMING: "bg-violet-500/10 text-violet-600 border-violet-500/30",
  AWAITING_CONFIRMATION: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  PARTIALLY_FILLED: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  UNKNOWN: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  REJECTED: "bg-red-500/10 text-red-600 border-red-500/30",
  INVALIDATED: "bg-red-500/10 text-red-600 border-red-500/30",
  CANCELLED: "bg-slate-500/10 text-slate-600 border-slate-500/30",
  EXPIRED: "bg-slate-500/10 text-slate-600 border-slate-500/30",
  CLOSED: "bg-slate-500/10 text-slate-600 border-slate-500/30",
  NO_DATA: "bg-slate-500/10 text-slate-600 border-slate-500/30",
};

export const Tag = ({ v, testid }) => (
  <span data-testid={testid} className={`mono inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-medium ${TONES[v] || "border-slate-600/40 bg-slate-700/20 text-slate-700"}`}>{v}</span>
);

export const Spinner = () => <Loader2 className="h-4 w-4 animate-spin text-slate-600" />;

export const Empty = ({ children }) => <div className="py-8 text-center text-xs text-slate-500">{children}</div>;

export const PageHead = ({ title, sub, right }) => (
  <div className="mb-4 flex flex-wrap items-end justify-between gap-3 fade-up">
    <div>
      <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
      {sub && <p className="mt-1 max-w-3xl text-xs text-slate-600 sm:text-sm">{sub}</p>}
    </div>
    {right}
  </div>
);

export const Btn = ({ children, variant = "primary", className = "", ...p }) => {
  const v = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white border-blue-500",
    ghost: "bg-transparent hover:bg-[#F1F5F9] text-slate-700 border-[#E2E8F0]",
    danger: "bg-red-600 hover:bg-red-700 text-white border-red-500",
    success: "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-500",
  }[variant];
  return (
    <button {...p} className={`inline-flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-xs font-medium transition-colors duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:border-[#E2E8F0] disabled:bg-[#F1F5F9] disabled:text-slate-500 ${v} ${className}`}>
      {children}
    </button>
  );
};

export const Fresh = ({ meta, testid }) => {
  if (!meta) return null;
  const rt = meta.realtime;
  const age = meta.age_minutes;
  const tone = age == null ? "text-slate-500" : age < 30 ? "text-emerald-600" : age < 120 ? "text-amber-600" : "text-slate-500";
  return (
    <span data-testid={testid} className={`mono text-[10px] ${tone}`} title={`Last bar ${meta.last_bar} · source ${meta.source}`}>
      {rt ? "RT·IEX" : `DELAYED ~${meta.delay_minutes || 15}m`} · {age == null ? "no data" : `${age}m old`}
    </span>
  );
};
